import get from 'lodash/get.js';
import { formatFullName } from '../utils/index.js';

export default {
    methods: {
        /**
         * Replaces placeholders with appropriate values
         *
         * @WARNING Duplicate PHP function in app/Jobs/SendAppointmentsConfirmation.php
         *
         * @return string
         */
        fillTemplateValues(content, obj, appendedMessage, cleanupPlaceholders = true) {
            if ((!content || typeof obj == 'undefined') && !appendedMessage) {
                return '';
            }

            const user = this.$store.state.authUser;

            if (!user) {
                return content;
            }

            const locale = this.getLocale(obj, user);

            // User
            content = this.fillPlaceholder(content, 'user.firstName', user.first_name);
            content = this.fillPlaceholder(content, 'user.lastName', user.last_name);
            content = this.fillPlaceholder(content, 'user.fullName', formatFullName(user));
            content = this.fillPlaceholder(content, 'user.email', user.email);
            // content = this.fillPlaceholder(content, 'user.phone', '');

            // Dealer
            content = this.fillPlaceholder(content, 'dealer.name', user.account.name);
            content = this.fillPlaceholder(content, 'dealer.phone', this.formatPhone(user.account.phone));
            content = this.fillPlaceholder(content, 'dealer.address', user.account.address);
            content = this.fillPlaceholder(content, 'dealer.city', user.account.city);
            content = this.fillPlaceholder(content, 'dealer.province', this.getProvinceOrStateString(user.account, locale));
            content = this.fillPlaceholder(content, 'dealer.postalCode', this.getPostalCodeOrZip(user.account));
            content = this.fillPlaceholder(content, 'dealer.country', this.getCountryString(user.account.country, locale));

            // Client
            if (typeof obj.contact !== 'undefined') {
                content = this.fillPlaceholder(content, 'client.civility', this.getCivility(obj.contact.civility, locale));
                content = this.fillPlaceholder(content, 'client.firstName', obj.contact.first_name);
                content = this.fillPlaceholder(content, 'client.lastName', obj.contact.last_name);
                content = this.fillPlaceholder(content, 'client.fullName', formatFullName(obj.contact));
                // content = this.fillPlaceholder(content, 'client.phone', this.formatPhone(obj.number));
            } else if (typeof obj.contact_phone !== 'undefined' && typeof obj.contact_phone.contact !== 'undefined') {
                content = this.fillPlaceholder(content, 'client.civility', this.getCivility(obj.contact_phone.contact.civility, locale));
                content = this.fillPlaceholder(content, 'client.firstName', obj.contact_phone.contact.first_name);
                content = this.fillPlaceholder(content, 'client.lastName', obj.contact_phone.contact.last_name);
                content = this.fillPlaceholder(content, 'client.fullName', formatFullName(obj.contact_phone.contact));
                // content = this.fillPlaceholder(content, 'client.phone', this.formatPhone(obj.number));
            } else {
                content = this.fillPlaceholder(content, 'client.civility', this.getCivility(obj.civility, locale));
                content = this.fillPlaceholder(content, 'client.firstName', obj.first_name);
                content = this.fillPlaceholder(content, 'client.lastName', obj.last_name);
                content = this.fillPlaceholder(content, 'client.fullName', formatFullName(obj));
                // content = this.fillPlaceholder(content, 'client.phone', this.formatPhone(validPhone ? validPhone.number : obj.phones[0].number));
            }

            // Appointment
            if (typeof obj.datetime !== 'undefined') {
                content = this.fillPlaceholder(content, 'appointment.date', this.formatDate(obj.datetime, locale));
                content = this.fillPlaceholder(content, 'appointment.time', this.formatTime(obj.datetime));
            }

            // Payment
            if (typeof obj.amount !== 'undefined') {
                content = this.fillPlaceholder(content, 'payment.amount', this.$n(obj.amount / 100, 'currency', locale));
                content = this.fillPlaceholder(content, 'payment.invoiceNumber', obj.invoice_number);
            }

            // Custom Placeholders
            content = this.fillCampaignPlaceholders(content, obj);
            content = this.fillCrmPlaceholders(content, obj);

            // Cleanup Placeholder tags
            if (cleanupPlaceholders) {
                content = this.cleanupRemainingPlaceholders(content);
            }

            // Add automated message
            if (appendedMessage && user.options[`${appendedMessage}_message`] && user.options[`${appendedMessage}_message`][locale]) {
                if (!empty(content.trim())) {
                    content += '\n';
                }

                content += user.options[`${appendedMessage}_message`][locale];

                if (appendedMessage === 'payment') {
                    content += locale === 'en' ? ' { secured-link }' : ' { lien-sécurisé }';
                }
            }

            return content.trim();
        },

        /**
         * Replaces the placeholder with the value in the content
         *
         * @WARNING Duplicate PHP function in app/Jobs/SendAppointmentsConfirmation.php
         *
         * @param content
         * @param keypath
         * @param value
         *
         * @return string
         */
        fillPlaceholder(content, keypath, value) {
            const templatePlaceholders = this.$store.state.templatePlaceholders;

            if (empty(templatePlaceholders)) {
                return content;
            }

            // Make sure value is correctly formatted
            if (empty(value)) {
                value = '';
            }

            value = value.trim();

            return content
                .replace(
                    new RegExp(`{ ${get(templatePlaceholders.fr, keypath)} }`, 'g'),
                    value,
                )
                .replace(
                    new RegExp(`{ ${get(templatePlaceholders.en, keypath)} }`, 'g'),
                    value,
                );
        },

        fillCampaignPlaceholders(content, obj) {
            const placeholders = (obj.placeholders || []).filter(placeholder => {
                return !!placeholder.campaign_id;
            });

            if (!placeholders) {
                return content;
            }

            for (const placeholder of placeholders) {
                const value = placeholder.value || placeholder.pivot?.value;

                content = this.fillCustomPlaceholder(content, placeholder.name, value);
            }

            return content;
        },

        fillCrmPlaceholders(content, obj) {
            const placeholders = (obj.placeholders || obj.contact?.placeholders || obj.contact_phone?.contact?.placeholders || [])
                .filter(placeholder => {
                    return !placeholder.campaign_id;
                });

            if (!placeholders) {
                return content;
            }

            for (const placeholder of placeholders) {
                const category = placeholder.original_name.split(':')[0];
                const key = placeholder.original_name.split(':')[1];
                let value = placeholder.value || placeholder.pivot?.value;

                if (placeholder.type === 'datetime' && value) {
                    const timeFormat = this.$i18n.locale == 'en' ? 'hh:mm A' : 'HH:mm';
                    value = moment(value).format(`D MMM YYYY ${timeFormat}`);
                }

                content = this.fillPlaceholder(content, `${category}.${key}`, value);
            }

            return content;
        },

        cleanupRemainingPlaceholders(content) {
            const templatePlaceholders = this.$store.state.templatePlaceholders;

            Object.values(templatePlaceholders).forEach(placeholderGroup => {
                Object.values(placeholderGroup).forEach(placeholderCategory => {
                    Object.values(placeholderCategory).forEach(placeholder => {
                        content = content.replace(
                            new RegExp(`{ ${placeholder} }`, 'g'),
                            '',
                        );
                    });
                });
            });

            return content;
        },

        /**
         * Replaces the custom placeholder with the value
         *
         *
         * @param content
         * @param placeholder
         * @param value
         *
         * @return string
         */
        fillCustomPlaceholder(content, placeholder, value) {
            // Make sure value is correctly formatted
            if (empty(value)) {
                value = '';
            }

            value = value.trim();

            return content
                .replace(
                    new RegExp(`{ ${placeholder} }`, 'g'),
                    value,
                );
        },

        /**
         * If a single language is set for a message, duplicates the translation to other languages.
         */
        duplicateTranslationIfEmpty(messageObj) {
            if (empty(messageObj.fr)) {
                messageObj.fr = messageObj.en;
            } else if (empty(messageObj.en)) {
                messageObj.en = messageObj.fr;
            }

            return messageObj;
        },

        getLocale(obj, user) {
            if (typeof obj.contact !== 'undefined') {
                return obj.contact.locale;
            } if (typeof obj.contact_phone !== 'undefined' && typeof obj.contact_phone.contact !== 'undefined') {
                return obj.contact_phone.contact.locale;
            }

            return user.locale;
        },

        getCivility(civility, locale) {
            return !empty(civility) ? this.$t(`global.civility.${civility}`, locale) : '';
        },
    },
};
